import { useMemo } from 'react'
import { map, reduce } from 'ramda'

import toFixedValue from 'app/common/lib/toFixedValue'

import { Allowance } from '../../_common/useReportAllowances'
import { AllSchoolsAllowancesDto } from '../../_common/useReportAllSchoolsAllowancesDto'
import { TotalsDto } from '../../_common/useReportTotalsDto'
import { useSubsidyType } from '../../_common/useSubsidyType'

import {
  ColumnKey,
  columnKeysLeft,
  columnKeysLeftHeaders,
  createColumnKeysMain,
  createColumnKeysMainHeaders,
  createHeaderCells,
  createDinamicColumnKey,
  createHeaderCellsProps,
  createCellsProps,
  createTotalCellsProps,
  columnKeysLeftHeadersNoSpan,
} from './tableColumns'

type Options = {
  allowances: Array<Allowance>
  allSchoolsAllowancesDto: AllSchoolsAllowancesDto
  totalsDto: TotalsDto
}

export function useByAllowancesTableData({
  allowances,
  allSchoolsAllowancesDto,
  totalsDto,
}: Options) {
  const subsidyType = useSubsidyType()

  const res = useMemo(() => {
    return {
      columnKeysLeft,
      columnKeysLeftHeaders: !subsidyType.showPersonal
        ? columnKeysLeftHeadersNoSpan
        : columnKeysLeftHeaders,
      columnKeysMain: createColumnKeysMain(allowances, subsidyType),
      columnKeysMainHeaders: createColumnKeysMainHeaders(allowances, subsidyType),
      header: createHeaderCells(allowances),
      headerProps: createHeaderCellsProps(allowances, subsidyType),
      data: map(
        ({ school_title: title, data: { common, total, allowances: byAllowance } }) => ({
          rowKey: title,
          rowData: reduce(
            (acc, allowance) => {
              const value = toFixedValue(byAllowance[allowance])
              const key = createDinamicColumnKey(allowance)
              acc[key] = { columnKey: key, value }
              return acc
            },
            {
              [ColumnKey.Org]: { columnKey: ColumnKey.Org as const, value: title },
              [ColumnKey.Common]: {
                columnKey: ColumnKey.Common as const,
                value: toFixedValue(common),
              },
              [ColumnKey.Total]: {
                columnKey: ColumnKey.Total as const,
                value: toFixedValue(total),
              },
            },
            allowances,
          ),
        }),
        allSchoolsAllowancesDto,
      ),
      dataProps: createCellsProps(allowances),
      total: [
        {
          rowKey: 'total',
          rowData: reduce(
            (acc, allowance) => {
              const value = toFixedValue(totalsDto.allowances[allowance])
              const key = createDinamicColumnKey(allowance)
              acc[key] = { columnKey: key, value }
              return acc
            },
            {
              [ColumnKey.Org]: {
                columnKey: ColumnKey.Org as const,
                value: 'Итого:',
              },
              [ColumnKey.Common]: {
                columnKey: ColumnKey.Common as const,
                value: toFixedValue(totalsDto.totalCommon),
              },
              [ColumnKey.Total]: {
                columnKey: ColumnKey.Total as const,
                value: toFixedValue(totalsDto.total),
              },
            },
            allowances,
          ),
        },
      ],
      totalProps: createTotalCellsProps(allowances),
    }
  }, [allowances, allSchoolsAllowancesDto, totalsDto, subsidyType])

  return res
}
